/* ==========================================================================
   JADUZAR INK — medical questionnaire document builder.

   Reads a submission e-mail body and renders it as a document that can be
   printed to PDF and forwarded to the anesthesia partner.

   Nothing leaves the browser: no fetch, no storage, no logging. The pasted
   text is parsed in memory and written into the page, and Clear removes it.

   The parse is deterministic by construction. Questions arrive as Qnn / Ann
   pairs, so an answer is bound to its own number and cannot slide under the
   next question however many lines it runs to. Anything unrecognised is
   reported on screen rather than dropped in silence.
   ========================================================================== */
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ------------------------------------------------------------- parsing */

  function parse(text) {
    const lines = String(text).replace(/\r\n?/g, '\n').split('\n');
    const doc = {
      reference: '',
      summary: [],
      consent: [],
      mediaStatus: '',
      sections: [],
      questions: 0,
      problems: [],
    };

    let bucket = null;      // 'summary' | 'consent'
    let section = null;     // current question section
    let pending = null;     // question waiting for its answer
    let expected = null;    // the number that answer must carry

    const facts = (line) => {
      // "  Label                 value"
      const m = line.match(/^ {2}(\S.*?) {2,}(.*)$/);
      return m ? { label: m[1].trim(), value: m[2].trim() } : null;
    };

    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i];
      const line = raw.replace(/\s+$/, '');

      if (/^SUMMARY$/.test(line)) { bucket = 'summary'; continue; }
      if (/^CONSENT$/.test(line)) { bucket = 'consent'; continue; }
      if (/^STATUS$/.test(line)) { bucket = null; continue; }

      const media = line.match(/^MEDIA STATUS:\s*(.+)$/);
      if (media) {
        doc.mediaStatus = media[1].trim();
        bucket = 'consent';
        continue;
      }

      const head = line.match(/^---\s+(.+?)\s+-{3,}\s*$/);
      if (head) {
        const name = head[1].trim();
        if (/^END OF QUESTIONNAIRE/i.test(name)) { section = null; bucket = null; continue; }
        section = { name, rows: [] };
        doc.sections.push(section);
        bucket = null;
        continue;
      }

      const q = line.match(/^Q(\d{2})\s{2}(.*)$/);
      if (q) {
        pending = { number: q[1], question: q[2].trim(), answer: [] };
        expected = q[1];
        if (!section) {
          section = { name: 'Questionnaire', rows: [] };
          doc.sections.push(section);
        }
        section.rows.push(pending);
        doc.questions += 1;
        continue;
      }

      const a = line.match(/^A(\d{2})\s{2}(.*)$/);
      if (a) {
        if (!pending || a[1] !== expected) {
          doc.problems.push('Answer A' + a[1] + ' did not follow question Q' + a[1] + '.');
          pending = null;
          expected = null;
          continue;
        }
        pending.answer.push(a[2]);
        continue;
      }

      // A continuation line of the answer currently being read.
      if (pending && /^ {6}\S/.test(raw)) {
        pending.answer.push(raw.trim());
        continue;
      }
      if (line === '') { pending = null; expected = null; continue; }

      if (bucket) {
        const f = facts(raw);
        if (f) doc[bucket].push(f);
      }
    }

    const ref = doc.summary.find((f) => /^reference$/i.test(f.label));
    doc.reference = ref ? ref.value : '';

    if (!doc.questions) doc.problems.push('No questions were found. Paste the whole e-mail body, starting at [MEDICAL REVIEW REQUIRED].');
    if (!doc.reference) doc.problems.push('No reference number was found in the summary.');

    // Numbers must be unique and ascending — the guarantee that matters most.
    const numbers = doc.sections.flatMap((s) => s.rows.map((r) => Number(r.number)));
    const dupes = numbers.filter((n, i) => numbers.indexOf(n) !== i);
    if (dupes.length) doc.problems.push('Question numbers appear more than once: ' + [...new Set(dupes)].join(', ') + '.');
    for (let i = 1; i < numbers.length; i++) {
      if (numbers[i] < numbers[i - 1]) {
        doc.problems.push('Question order is not ascending around number ' + numbers[i] + '.');
        break;
      }
    }

    return doc;
  }

  /* ------------------------------------------------------------ rendering */

  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  };

  function facts(list, into) {
    into.textContent = '';
    list.forEach((f) => {
      const row = el('div', 'jimd__fact');
      row.appendChild(el('dt', null, f.label));
      row.appendChild(el('dd', null, f.value || '—'));
      into.appendChild(row);
    });
  }

  function render(doc, root) {
    $('[data-jimd-ref]', root).textContent = doc.reference || '—';
    $('[data-jimd-running]', root).textContent =
      'JADUZAR INK · Medical questionnaire · ' + (doc.reference || '');

    const status = $('[data-jimd-status]', root);
    status.textContent =
      'Awaiting JADUZAR INK review — this is not medical approval. The responsible doctor makes the final medical decision.';

    facts(doc.summary, $('[data-jimd-summary]', root));

    const consent = doc.mediaStatus
      ? [{ label: 'Media status', value: doc.mediaStatus }].concat(doc.consent)
      : doc.consent;
    facts(consent, $('[data-jimd-consent]', root));

    const host = $('[data-jimd-sections]', root);
    host.textContent = '';
    doc.sections.forEach((section) => {
      if (!section.rows.length) return;
      const block = el('section', 'jimd__block');
      block.appendChild(el('h2', 'jimd__h2', section.name));

      const list = el('div', 'jimd__qa');
      section.rows.forEach((r) => {
        const answer = r.answer.join('\n').trim();
        const short = answer.length <= 24 && !answer.includes('\n');

        const row = el('div', 'jimd__row' + (short ? ' jimd__row--short' : ''));
        const q = el('p', 'jimd__q');
        q.appendChild(el('b', 'jimd__n', r.number));
        q.appendChild(document.createTextNode(r.question));

        const a = el('p', 'jimd__a' + (/^(yes|no|ja|nein|nee)$/i.test(answer) ? ' jimd__a--flag' : ''));
        a.textContent = answer || '—';

        row.appendChild(q);
        row.appendChild(a);
        list.appendChild(row);
      });

      block.appendChild(list);
      host.appendChild(block);
    });

    $('[data-jimd-footline]', root).textContent =
      doc.questions + ' questions · reference ' + (doc.reference || '—') +
      ' · this document contains health information and is intended only for JADUZAR INK and the responsible doctor.';
  }

  /* ----------------------------------------------------------------- boot */

  function setup(root) {
    const source = $('[data-jimd-source]', root);
    const doc = $('[data-jimd-doc]', root);
    const note = $('[data-jimd-note]', root);
    const hint = $('[data-jimd-hint]', root);
    const printBtn = $('[data-jimd-print]', root);
    const clearBtn = $('[data-jimd-clear]', root);
    const title = document.title;

    const build = () => {
      const text = source.value.trim();
      if (!text) {
        note.textContent = 'Paste the submission e-mail body first.';
        return;
      }
      const parsed = parse(text);
      if (!parsed.questions) {
        note.textContent = parsed.problems.join(' ');
        doc.hidden = true;
        printBtn.hidden = true;
        return;
      }

      render(parsed, doc);
      doc.hidden = false;
      printBtn.hidden = false;
      clearBtn.hidden = false;
      hint.hidden = false;

      /* The browser offers the document title as the PDF filename. The
         reference identifies the client without putting a name, a date of
         birth or a condition into a filename that will be forwarded. */
      document.title = 'JADUZAR-INK-Medical-Questionnaire-' + (parsed.reference || 'UNREFERENCED');

      note.textContent = parsed.problems.length
        ? 'Built with ' + parsed.questions + ' questions. Check these first: ' + parsed.problems.join(' ')
        : 'Built ' + parsed.questions + ' questions in ' + parsed.sections.length + ' sections. Nothing was left unmatched.';
      note.className = 'jimd__note' + (parsed.problems.length ? ' jimd__note--warn' : ' jimd__note--ok');
    };

    $('[data-jimd-build]', root).addEventListener('click', build);
    printBtn.addEventListener('click', () => window.print());
    clearBtn.addEventListener('click', () => {
      source.value = '';
      doc.hidden = true;
      printBtn.hidden = true;
      clearBtn.hidden = true;
      hint.hidden = true;
      $('[data-jimd-sections]', doc).textContent = '';
      $('[data-jimd-summary]', doc).textContent = '';
      $('[data-jimd-consent]', doc).textContent = '';
      document.title = title;
      note.textContent = 'Cleared. Nothing from that submission is left on this page.';
      note.className = 'jimd__note';
      source.focus();
    });
  }

  const init = () => $$('[data-jimd]').forEach(setup);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
